import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Search, 
  Calculator, 
  FileText, 
  Download, 
  Lightbulb,
  BarChart3,
  Bot,
  Zap,
  BookOpen
} from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { AnimatedSection } from "@/components/ui/AnimatedSection";
import { AnimatedBackground } from "@/components/ui/AnimatedBackground";
import { FloatingShapes } from "@/components/ui/GlowingOrb";
import { TextReveal } from "@/components/ui/TextReveal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useState } from "react";

const featuredResources = [
  {
    title: "SaaS GTM Blueprint",
    description: "Complete AI-first go-to-market framework for SaaS companies",
    href: "/resources/saas-gtm-blueprint",
    icon: Zap,
    badge: "Guide",
    badgeColor: "bg-blue-500/10 text-blue-500",
  },
  {
    title: "Search Growth Framework",
    description: "Master both traditional SEO and AI-powered search with this comprehensive playbook",
    href: "/resources/search-growth-framework",
    icon: Search,
    badge: "Framework",
    badgeColor: "bg-primary/10 text-primary",
  },
  {
    title: "Free AI Growth Audit",
    description: "Discover your visibility in AI search engines and get a custom roadmap",
    href: "/tools/ai-growth-audit",
    icon: BarChart3,
    badge: "Free Tool",
    badgeColor: "bg-green-500/10 text-green-500",
  },
];

const tools = [
  {
    title: "AI Growth Audit",
    description: "Comprehensive AI search visibility assessment",
    href: "/tools/ai-growth-audit",
    icon: Bot,
    type: "Free Audit",
  },
  {
    title: "AI SEO & GEO Audit",
    description: "Complete Google + AI search analysis",
    href: "/tools/ai-seo-geo-audit",
    icon: Search,
    type: "Free Audit",
  },
  {
    title: "ROI Calculator",
    description: "Calculate your potential AI marketing ROI",
    href: "/roi-calculator",
    icon: Calculator,
    type: "Calculator",
  },
];

const guides = [
  {
    title: "SaaS GTM Blueprint",
    description: "AI-first go-to-market strategy framework",
    href: "/resources/saas-gtm-blueprint",
    icon: Zap,
    type: "Guide",
  },
  {
    title: "Search Growth Framework",
    description: "GEO implementation and optimization playbook",
    href: "/resources/search-growth-framework",
    icon: FileText,
    type: "Framework",
  },
];

const categories = [
  {
    title: "Tools & Audits",
    description: "Free tools to assess your AI marketing readiness",
    items: tools,
    icon: Bot,
  },
  {
    title: "Guides & Frameworks",
    description: "Strategic resources for AI-first marketing",
    items: guides,
    icon: BookOpen,
  },
];

const resourceHubSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "Resource Hub | TheCloudSEO",
  "description": "Tools, guides, and frameworks for AI-powered marketing success",
  "url": "https://thecloudseo.com/resources",
  "publisher": {
    "@type": "Organization",
    "name": "The Cloud SEO",
    "url": "https://thecloudseo.com"
  }
};

export default function ResourceHub() {
  const [searchQuery, setSearchQuery] = useState("");

  const allResources = [...tools, ...guides];
  const filteredResources = searchQuery
    ? allResources.filter(
        (r) =>
          r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : null;

  return (
    <Layout>
      <SEO
        title="Resource Hub | Tools, Guides & Frameworks"
        description="Free tools, guides, and frameworks to accelerate your AI-powered marketing. Get audits, calculators, and strategic resources."
        canonical="https://thecloudseo.com/resources"
        jsonLd={resourceHubSchema}
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <AnimatedBackground variant="mesh" />
        <FloatingShapes />

        <div className="container-wide relative z-10">
          <Breadcrumbs
            items={[
              { label: "Home", href: "/" },
              { label: "Resources" },
            ]}
            className="mb-8"
          />

          <div className="max-w-3xl">
            <TextReveal>
              <h1 className="heading-hero mb-6">Resource Hub</h1>
            </TextReveal>
            <TextReveal delay={0.1}>
              <p className="text-xl text-muted-foreground mb-8">
                Tools, guides, and frameworks to accelerate your AI-powered marketing journey. 
                Free resources designed for B2B and SaaS growth leaders.
              </p>
            </TextReveal>

            {/* Search */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="relative max-w-md"
            >
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 rounded-full bg-background/80 backdrop-blur border-border"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Search Results */}
      {filteredResources && (
        <section className="py-12 border-b border-border">
          <div className="container-wide">
            <h2 className="text-lg font-semibold mb-6">
              {filteredResources.length} result{filteredResources.length !== 1 ? "s" : ""} for "{searchQuery}"
            </h2>
            {filteredResources.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredResources.map((resource, index) => (
                  <Link
                    key={index}
                    to={resource.href}
                    className="group p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-primary/10">
                        <resource.icon className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <Badge variant="secondary" className="mb-2 text-xs">
                          {resource.type}
                        </Badge>
                        <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors">
                          {resource.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">{resource.description}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">No resources found. Try a different search term.</p>
            )}
          </div>
        </section>
      )}

      {/* Featured Resources */}
      {!filteredResources && (
        <section className="section-spacing bg-secondary/30">
          <div className="container-wide">
            <AnimatedSection className="text-center mb-12">
              <Badge className="mb-4 bg-primary/10 text-primary">
                <Lightbulb className="w-3 h-3 mr-1" />
                Featured
              </Badge>
              <TextReveal>
                <h2 className="heading-section mb-4">Start Here</h2>
              </TextReveal>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Our most popular resources to kickstart your AI marketing journey
              </p>
            </AnimatedSection>

            <div className="grid md:grid-cols-3 gap-8">
              {featuredResources.map((resource, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={resource.href}
                    className="group block p-8 rounded-3xl bg-card border border-border hover:border-primary/50 transition-all h-full"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-xl bg-primary/10">
                        <resource.icon className="w-8 h-8 text-primary" />
                      </div>
                      <Badge className={resource.badgeColor}>
                        {resource.badge}
                      </Badge>
                    </div>
                    <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                      {resource.title}
                    </h3>
                    <p className="text-muted-foreground mb-4">{resource.description}</p>
                    <span className="inline-flex items-center text-primary font-medium">
                      Explore <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories */}
      {!filteredResources && (
        <section className="section-spacing">
          <div className="container-wide">
            <div className="space-y-16">
              {categories.map((category, catIndex) => (
                <AnimatedSection key={catIndex} delay={catIndex * 0.1}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <category.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">{category.title}</h2>
                      <p className="text-muted-foreground">{category.description}</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {category.items.map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link
                          to={item.href}
                          className="group flex items-start gap-4 p-6 rounded-2xl bg-secondary/30 border border-border hover:border-primary/50 transition-all"
                        >
                          <div className="p-3 rounded-xl bg-background">
                            <item.icon className="w-6 h-6 text-primary" />
                          </div>
                          <div className="flex-1">
                            <Badge variant="outline" className="mb-2 text-xs">
                              {item.type}
                            </Badge>
                            <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors">
                              {item.title}
                            </h3>
                            <p className="text-sm text-muted-foreground">{item.description}</p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all mt-1" />
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quick Links */}
      {!filteredResources && (
        <section className="section-spacing bg-secondary/30">
          <div className="container-wide">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Case Studies */}
              <AnimatedSection>
                <Link
                  to="/case-studies"
                  className="group block p-8 rounded-3xl bg-card border border-border hover:border-primary/50 transition-all h-full"
                >
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                    Case Studies
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    See how we've helped B2B and SaaS companies achieve AI-powered growth
                  </p>
                  <span className="inline-flex items-center text-primary font-medium">
                    View Case Studies <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </AnimatedSection>

              {/* Insights */}
              <AnimatedSection delay={0.1}>
                <Link
                  to="/insights"
                  className="group block p-8 rounded-3xl bg-card border border-border hover:border-primary/50 transition-all h-full"
                >
                  <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                    Insights & Articles
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Expert articles on GEO, AI search, content marketing, and automation
                  </p>
                  <span className="inline-flex items-center text-primary font-medium">
                    Browse Insights <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </AnimatedSection>
            </div>
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="section-spacing bg-primary text-primary-foreground relative overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />
        <div className="container-narrow text-center relative z-10">
          <AnimatedSection>
            <TextReveal>
              <h2 className="heading-section mb-6">Stay Updated</h2>
            </TextReveal>
            <p className="text-xl text-primary-foreground/80 max-w-2xl mx-auto mb-8">
              Get the latest AI marketing insights, tools, and strategies delivered to your inbox.
            </p>
            <Button
              size="lg"
              variant="secondary"
              className="rounded-full px-10 text-base h-14"
              asChild
            >
              <a 
                href="https://thecloudseo.beehiiv.com/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Subscribe to Newsletter <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
